/* eslint-disable max-len */
/* eslint-disable prettier/prettier */
import { z } from 'zod';

/**
 * Chart Data Point Schema
 * Represents individual data points in all chart types
 */
const ChartDataPointSchema = z.object({
  name: z.string().min(1, "Data point name is required - use meaningful labels like 'Q1 2024', 'North America', 'Product A'"),
  value: z.number().describe("Data point value - can be positive or negative to represent the actual data value to plot"),
  color: z.string().optional().describe("Optional hex color code (e.g., '#FF5733') to override default chart colors for this specific data point")
});

/**
 * Bar Chart Configuration Schema
 * Controls visual appearance and behavior of bar charts
 */
const BarChartConfigSchema = z.object({
  horizontal: z.boolean().optional().describe("Set to true for horizontal bars (useful for long category names), false for vertical bars (default)"),
  showValues: z.boolean().optional().describe("Set to true to display actual values on top of bars for better readability"),
  colors: z.array(z.string()).optional().describe("Array of hex color codes (e.g., ['#FF5733', '#33FF57']) to customize bar colors")
}).optional();

/**
 * Line Chart Configuration Schema
 * Controls visual appearance and behavior of line charts
 */
const LineChartConfigSchema = z.object({
  smooth: z.boolean().optional().describe("Set to true for smooth curved lines, false for straight line segments between points"),
  showArea: z.boolean().optional().describe("Set to true to fill area under the line with gradient color for emphasis"),
  colors: z.array(z.string()).optional().describe("Array of hex color codes for line and area colors")
}).optional();

/**
 * Pie Chart Configuration Schema
 * Controls visual appearance and behavior of pie charts
 */
const PieChartConfigSchema = z.object({
  showLabels: z.boolean().optional().describe("Set to true to display slice labels with percentages outside the pie chart"),
  colors: z.array(z.string()).optional().describe("Array of hex color codes for pie slices - should match number of data points")
}).optional();

/**
 * Donut Chart Configuration Schema
 * Controls visual appearance and behavior of donut charts (pie charts with hollow center)
 */
const DonutChartConfigSchema = z.object({
  showLabels: z.boolean().optional().describe("Set to true to display slice labels with percentages outside the donut chart"),
  innerRadius: z.number().min(0).max(100).optional().describe("Percentage of inner radius (0-100) - higher values create larger hollow center"),
  colors: z.array(z.string()).optional().describe("Array of hex color codes for donut slices")
}).optional();

/**
 * Area Chart Configuration Schema
 * Controls visual appearance and behavior of area charts
 */
const AreaChartConfigSchema = z.object({
  smooth: z.boolean().optional().describe("Set to true for smooth curved area boundaries, false for straight line segments"),
  opacity: z.number().min(0).max(1).optional().describe("Area fill opacity (0.0 to 1.0) - 0.3 provides good balance between visibility and readability"),
  colors: z.array(z.string()).optional().describe("Array of hex color codes for area fill and border")
}).optional();

/**
 * Scatter Plot Data Point Schema
 * Represents individual data points with x,y coordinates for scatter plots
 */
const ScatterDataPointSchema = z.object({
  name: z.string().min(1, "Data point name is required - use meaningful labels like 'Customer A', 'Region 1', 'Product X'"),
  x: z.number().describe("X-axis value for positioning the data point horizontally"),
  y: z.number().describe("Y-axis value for positioning the data point vertically"),
  color: z.string().optional().describe("Optional hex color code (e.g., '#FF5733') to override default chart colors for this specific data point")
});

/**
 * Scatter Chart Configuration Schema
 * Controls visual appearance and behavior of scatter plots
 */
const ScatterChartConfigSchema = z.object({
  xAxisLabel: z.string().optional().describe("Label for X-axis (e.g., 'Revenue', 'Age', 'Temperature')"),
  yAxisLabel: z.string().optional().describe("Label for Y-axis (e.g., 'Profit', 'Performance', 'Growth Rate')"),
  symbolSize: z.number().min(1).max(20).optional().describe("Size of scatter plot points (1-20) - larger values for more emphasis"),
  colors: z.array(z.string()).optional().describe("Array of hex color codes for scatter plot points")
}).optional();

/**
 * Multi-Series Data Point Schema
 * Represents data points with multiple values for different series
 */
const MultiSeriesDataPointSchema = z.object({
  name: z.string().min(1, "Category name is required - use meaningful labels like 'Q1 2024', 'North America', 'Product Line A'"),
  values: z.record(z.string(), z.number()).describe("Object mapping series names to their values (e.g., {'Profit': 1000, 'Loss': -500})")
});

/**
 * Stacked Bar Chart Configuration Schema
 * Controls visual appearance and behavior of stacked bar charts
 */
const StackedBarChartConfigSchema = z.object({
  horizontal: z.boolean().optional().describe("Set to true for horizontal stacked bars, false for vertical stacked bars (default)"),
  showValues: z.boolean().optional().describe("Set to true to display individual values on each stack segment"),
  colors: z.array(z.string()).optional().describe("Array of hex color codes for different series - should match number of series")
}).optional();

/**
 * Multiset Bar Chart Configuration Schema
 * Controls visual appearance and behavior of grouped/multiset bar charts
 */
const MultisetBarChartConfigSchema = z.object({
  horizontal: z.boolean().optional().describe("Set to true for horizontal grouped bars, false for vertical grouped bars (default)"),
  showValues: z.boolean().optional().describe("Set to true to display values on top of each bar in the group"),
  colors: z.array(z.string()).optional().describe("Array of hex color codes for different series - should match number of series")
}).optional();

/**
 * Individual Chart Type Schemas
 * Each schema represents a specific chart type with its unique properties
 */

/**
 * Bar Chart Schema - Best for comparing categories or showing rankings
 * Use for: Sales by region, product performance, survey results, etc.
 */
const BarChartSchema = z.object({
  chart_type: z.literal("bar").describe("Chart type identifier - must be 'bar' for bar charts"),
  title: z.string().optional().describe("Optional descriptive title for the chart (e.g., 'Q4 Sales Performance by Region')"),
  data: z.array(ChartDataPointSchema).min(1, "Bar chart requires at least one data point - each point represents a category with its value"),
  config: BarChartConfigSchema.describe("Optional configuration for bar chart appearance and behavior")
});

/**
 * Line Chart Schema - Best for showing trends over time or continuous data
 * Use for: Revenue trends, user growth, temperature changes, stock prices, etc.
 */
const LineChartSchema = z.object({
  chart_type: z.literal("line").describe("Chart type identifier - must be 'line' for line charts"),
  title: z.string().optional().describe("Optional descriptive title for the chart (e.g., 'Monthly Revenue Trend 2024')"),
  data: z.array(ChartDataPointSchema).min(1, "Line chart requires at least one data point - typically represents time series data"),
  config: LineChartConfigSchema.describe("Optional configuration for line chart appearance including smoothing and area fill")
});

/**
 * Pie Chart Schema - Best for showing parts of a whole (percentages/proportions)
 * Use for: Market share, budget allocation, survey responses, demographic breakdown, etc.
 */
const PieChartSchema = z.object({
  chart_type: z.literal("pie").describe("Chart type identifier - must be 'pie' for pie charts"),
  title: z.string().optional().describe("Optional descriptive title for the chart (e.g., 'Market Share by Product Category')"),
  data: z.array(ChartDataPointSchema).min(1, "Pie chart requires at least one data point - each point represents a slice of the total"),
  config: PieChartConfigSchema.describe("Optional configuration for pie chart labels and colors")
});

/**
 * Donut Chart Schema - Similar to pie chart but with hollow center for additional text/info
 * Use for: Same as pie charts but when you want space in center for totals or key metrics
 */
const DonutChartSchema = z.object({
  chart_type: z.literal("donut").describe("Chart type identifier - must be 'donut' for donut charts"),
  title: z.string().optional().describe("Optional descriptive title for the chart (e.g., 'Customer Satisfaction Distribution')"),
  data: z.array(ChartDataPointSchema).min(1, "Donut chart requires at least one data point - each point represents a segment"),
  config: DonutChartConfigSchema.describe("Optional configuration including inner radius size and label display")
});

/**
 * Area Chart Schema - Best for showing cumulative totals over time or emphasizing magnitude
 * Use for: Cumulative sales, stacked metrics, highlighting growth trends, etc.
 */
const AreaChartSchema = z.object({
  chart_type: z.literal("area").describe("Chart type identifier - must be 'area' for area charts"),
  title: z.string().optional().describe("Optional descriptive title for the chart (e.g., 'Customer Acquisition Growth')"),
  data: z.array(ChartDataPointSchema).min(1, "Area chart requires at least one data point - typically represents progressive/cumulative data"),
  config: AreaChartConfigSchema.describe("Optional configuration for area chart smoothing and fill opacity")
});

/**
 * Scatter Chart Schema - Best for showing correlation between two variables
 * Use for: Performance vs cost analysis, age vs income correlation, risk vs return plots, etc.
 */
const ScatterChartSchema = z.object({
  chart_type: z.literal("scatter").describe("Chart type identifier - must be 'scatter' for scatter plots"),
  title: z.string().optional().describe("Optional descriptive title for the chart (e.g., 'Revenue vs Profit Analysis')"),
  data: z.array(ScatterDataPointSchema).min(1, "Scatter chart requires at least one data point with x,y coordinates"),
  config: ScatterChartConfigSchema.describe("Optional configuration for axis labels and point appearance")
});

/**
 * Stacked Bar Chart Schema - Best for showing composition of categories over multiple series
 * Use for: Sales breakdown by product and region, budget allocation by department and quarter, etc.
 */
const StackedBarChartSchema = z.object({
  chart_type: z.literal("stackedBar").describe("Chart type identifier - must be 'stackedBar' for stacked bar charts"),
  title: z.string().optional().describe("Optional descriptive title for the chart (e.g., 'Quarterly Sales by Product Category')"),
  data: z.array(MultiSeriesDataPointSchema).min(1, "Stacked bar chart requires at least one category with multiple series values"),
  series: z.array(z.string()).min(1, "Series array is required - defines the different segments in each stack"),
  config: StackedBarChartConfigSchema.describe("Optional configuration for stacked bar chart orientation and values display")
});

/**
 * Multiset Bar Chart Schema - Best for comparing multiple series side-by-side across categories
 * Use for: Comparing performance metrics across teams, sales comparison by product and time period, etc.
 */
const MultisetBarChartSchema = z.object({
  chart_type: z.literal("multisetBar").describe("Chart type identifier - must be 'multisetBar' for grouped bar charts"),
  title: z.string().optional().describe("Optional descriptive title for the chart (e.g., 'Sales Performance Comparison by Quarter')"),
  data: z.array(MultiSeriesDataPointSchema).min(1, "Multiset bar chart requires at least one category with multiple series values"),
  series: z.array(z.string()).min(1, "Series array is required - defines the different bars in each group"),
  config: MultisetBarChartConfigSchema.describe("Optional configuration for grouped bar chart orientation and values display")
});

/**
 * Universal Chart Schema - Supports all chart types
 * This discriminated union automatically selects the correct schema based on chart_type
 * 
 * Available chart types:
 * - "bar": For comparing categories, rankings, or discrete data
 * - "line": For trends over time or continuous data series
 * - "pie": For showing parts of a whole (percentages/proportions)
 * - "donut": For parts of a whole with space for central information
 * - "area": For cumulative data or emphasizing magnitude of trends
 * - "scatter": For showing correlation between two variables (x,y coordinates)
 * - "stackedBar": For composition of categories across multiple series (stacked segments)
 * - "multisetBar": For comparing multiple series side-by-side (grouped bars)
 */
export const ChartSchema = z.discriminatedUnion("chart_type", [
  BarChartSchema,
  LineChartSchema,
  PieChartSchema,
  DonutChartSchema,
  AreaChartSchema,
  ScatterChartSchema,
  StackedBarChartSchema,
  MultisetBarChartSchema
]);

/**
 * Chart Validation Function - Validates chart data against the schema
 * 
 * @param chart - The chart object to validate
 * @returns Validated chart object with proper typing
 * @throws Error with descriptive message if validation fails
 * 
 * Use this function before rendering charts to ensure data integrity.
 * Provides clear error messages for LLM agents to understand what went wrong.
 */
export const validateChart = (chart: unknown) => {
  try {
    return ChartSchema.parse(chart);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const issues = error.issues.map(issue => {
        const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
        return `${path}${issue.message}`;
      });
      throw new Error(`Chart validation failed: ${issues.join(', ')}. Please check your chart data structure and ensure all required fields are provided with correct types.`);
    }
    throw new Error('Chart validation failed: Unknown error occurred during validation');
  }
};
